import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Input,
  Avatar,
  Text,
  Box,
  Container,
  Flex,
  Heading,
  List,
  Button,
  Label
} from "samba-ui";
import moment from "moment";
import styled from "styled-components";
import DeclineRegistrationModal from "./DeclineRegistrationModal";
import { loadRegistrations, updateRegistration } from "../redux/registrations";
import AddNewDashboardModal from "./AddNewDashboardModal";
import { APPLICATION_COLORS } from "../constants";

const StyledFlex = styled(Flex)`
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  pointer-events: ${p => (p.disabled ? "none" : "auto")};
`;

const ReviewRegistrations = ({
  registrations,
  loadRegistrations,
  updateRegistration,
  user
}) => {
  const isSuperOperator = user.role === "superoperator";
  const [isOpen, setIsOpen] = useState(false);
  const [isAddNewDashboardOpen, setIsAddNewDashboardOpen] = useState(false);
  const [selectedRegistrations, setSelectedRegistrations] = useState([]);
  const [selectedRegistrationIds, setSelectedRegistrationIds] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const req = async () => {
      await loadRegistrations();
    };
    req();
  }, [loadRegistrations]);

  const onRejectClick = id => {
    setIsOpen(true);
    setSelectedRegistrations([registrations[id]]);
  };

  const onApproveClick = id => {
    setIsAddNewDashboardOpen(true);
    setSelectedRegistrations([registrations[id]]);
  };

  const onApproveSelected = () => {
    setIsAddNewDashboardOpen(true);
    setSelectedRegistrations(
      Object.keys(selectedRegistrationIds).map(id => registrations[id])
    );
  };

  const onRejectSelected = () => {
    setIsOpen(true);
    setSelectedRegistrations(
      Object.keys(selectedRegistrationIds).map(id => registrations[id])
    );
  };

  const onDecline = async reason => {
    const ids = selectedRegistrations.map(r => r.id);
    await updateRegistration({ reason, status: "rejected", ids });
  };

  const onDashboardsSubmit = async dashboardFilters => {
    const ids = selectedRegistrations.map(r => r.id);
    setIsAddNewDashboardOpen(false);
    dashboardFilters = dashboardFilters.reduce((acc, { filters }) => {
      acc.push(filters.map(f => f.value));
      return acc;
    }, []);
    await updateRegistration({
      ids,
      status: "approved",
      filters: dashboardFilters
    });
  };

  const onSelectRegistration = (e, id) => {
    const { checked } = e.target;
    const newSelected = { ...selectedRegistrationIds, [id]: checked };
    setSelectedRegistrationIds(newSelected);
    const checkedRegistrations = Object.values(newSelected).filter(
      checked => checked
    );
    setSelectAll(
      checkedRegistrations.length === Object.values(newSelected).length
    );
  };

  const onClickSelectAll = e => {
    const { checked } = e.target;
    setSelectedRegistrationIds(
      Object.keys(registrations).reduce((acc, id) => {
        acc[id] = checked;
        return acc;
      }, {})
    );

    setSelectAll(checked);
  };

  return (
    <Container maxWidth={2048}>
      <Flex>
        <Box width={3 / 4} pr={4} minWidth={960}>
          <Box>
            <Flex mb={3} alignItems="flex-end">
              <Heading color="secondary" fontSize={3} fontWeight={500}>
                All Registrations
              </Heading>
            </Flex>
            <List.Header borderColor="#d6d6d6 !important">
              <Flex justifyContent="space-between" alignItems="center">
                <Box width={1 / 32}>
                  <Input
                    style={{ width: "auto" }}
                    type="checkbox"
                    checked={selectAll}
                    onChange={onClickSelectAll}
                  />
                </Box>
                <Box width={5 / 16}>
                  <Text fontSize={1} color="secondary" fontWeight={500}>
                    Registration Users
                  </Text>
                </Box>
                <Box width={2 / 16}>
                  <Text fontSize={1} color="secondary" fontWeight={500}>
                    Created At
                  </Text>
                </Box>
                <Box width={2 / 16}>
                  <Text fontSize={1} color="secondary" fontWeight={500}>
                    Company
                  </Text>
                </Box>
                <Box width={3 / 16}>
                  <Text fontSize={1} color="secondary" fontWeight={500}>
                    Application
                  </Text>
                </Box>
                <Box width={3 / 16} textAlign="center">
                  <Text fontSize={1} color="secondary" fontWeight={500}>
                    Actions
                  </Text>
                </Box>
              </Flex>
            </List.Header>
            {!Object.keys(registrations).length && (
              <List.Empty>No result found!</List.Empty>
            )}
            {Object.keys(registrations).map(id => {
              const {
                imageURL,
                name,
                email,
                companyName,
                application,
                createdAt
              } = registrations[id];

              return (
                <List.Item key={id} borderColor="#d6d6d6 !important">
                  <Flex alignItems="center" justifyContent="space-between">
                    <Box width={1 / 32}>
                      <Input
                        style={{ width: "auto" }}
                        type="checkbox"
                        checked={selectedRegistrationIds[id]}
                        onChange={e => onSelectRegistration(e, id)}
                      />
                    </Box>
                    <Flex width={5 / 16}>
                      <Avatar src={imageURL} mr={2} width={40} height={40} />
                      <Box>
                        <Box>
                          <Text fontSize={1} color="palette.ink.normal">
                            {name}
                          </Text>
                        </Box>
                        <Text fontSize={1} color="palette.ink.normal">
                          {email}
                        </Text>
                      </Box>
                    </Flex>
                    <Box width={2 / 16}>
                      <Text fontSize={1}>
                        {moment(createdAt).format("lll")}
                      </Text>
                    </Box>
                    <Box width={2 / 16}>
                      <Label>{companyName}</Label>
                    </Box>
                    <Box width={3 / 16}>
                      <Label color={APPLICATION_COLORS[application]}>
                        {application}
                      </Label>
                    </Box>
                    <StyledFlex
                      width={3 / 16}
                      alignItems="center"
                      justifyContent="space-between"
                      disabled={
                        isSuperOperator ||
                        Object.values(selectedRegistrationIds).filter(s => s)
                          .length
                      }
                    >
                      <Button
                        variant="destructiveBordered"
                        onClick={() => onRejectClick(id)}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="primaryBordered"
                        onClick={() => onApproveClick(id)}
                      >
                        Review
                      </Button>
                    </StyledFlex>
                  </Flex>
                </List.Item>
              );
            })}
            <List.Item borderColor="#d6d6d6 !important">
              <Flex alignItems="center" justifyContent="flex-end">
                <StyledFlex
                  justifyContent="space-between"
                  width={6 / 16}
                  disabled={
                    isSuperOperator ||
                    !Object.values(selectedRegistrationIds).filter(s => s)
                      .length
                  }
                >
                  <Button variant="destructive" onClick={onRejectSelected}>
                    Reject Selected
                  </Button>
                  <Button variant="primary" onClick={onApproveSelected}>
                    Review Selected
                  </Button>
                </StyledFlex>
              </Flex>
            </List.Item>
          </Box>
        </Box>
      </Flex>
      <DeclineRegistrationModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onDeny={onDecline}
        registrations={selectedRegistrations}
      />
      <AddNewDashboardModal
        isOpen={isAddNewDashboardOpen}
        onClose={() => setIsAddNewDashboardOpen(false)}
        registrations={selectedRegistrations}
        onSubmit={onDashboardsSubmit}
      />
    </Container>
  );
};

const mapStateToProps = state => ({
  registrations: state.registrations
});

const mapDispatchToProps = dispatch => ({
  loadRegistrations: () => dispatch(loadRegistrations()),
  updateRegistration: (id, body) => dispatch(updateRegistration(id, body))
});

ReviewRegistrations.propTypes = {
  registrations: PropTypes.object,
  loadRegistrations: PropTypes.func,
  updateRegistration: PropTypes.func,
  user: PropTypes.object
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReviewRegistrations);
